<template>
  <SubscribeForm
    v-model:email-address="emailAddress"
    v-model:metadata="metadata"
    v-model:turnstile-response-token="turnstileResponseToken"
    v-model:survey-responses="surveyResponses"
    :newsletter="newsletter"
    :subscriber="subscriber"
    :state="state"
    :variant="variant"
    :error="error"
    :recognized="recognized"
    :surveys="surveys"
    :subscribe="subscribe"
    :resubscribe="resubscribe"
    :check-subscriber="checkSubscriber"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { writable } from "@/lib/dirty";
import type { Newsletter } from "@/types/newsletter";

import { createTranslator } from "../lib/utils";
import { useResubscribe } from "./composables/useResubscribe";
import { useSubscribe } from "./composables/useSubscribe";
import { useSurveySubmission } from "./composables/useSurveySubmission";
import {
  type BasicSubscriber,
  type FormSurvey,
  type SubscribeFormVariant,
  type SubscriberFacingAuthentication,
  type SubscriptionState,
  calculateSubscriptionState,
  isValidEmail,
  parseError,
} from "./lib";
import SubscribeForm from "./SubscribeForm.vue";

interface Props {
  newsletter: Newsletter;
  subscriberFacingAuthentication?: SubscriberFacingAuthentication;
  variant: SubscribeFormVariant;
  onSubscribe?: (subscriber: BasicSubscriber) => void;
  surveys?: FormSurvey[];
  formId?: string | null;
}

const props = defineProps<Props>();

const t = createTranslator(() => props.newsletter.locale);

const emailAddress = ref(
  props.subscriberFacingAuthentication?.email_address || ""
);
const metadata = ref<Record<string, string>>({});
const surveyResponses = ref<Record<string, string | number | number[]>>({});
const turnstileResponseToken = ref("");
const subscriber = ref<BasicSubscriber | undefined>(
  props.subscriberFacingAuthentication?.subscribers?.find(
    (s) => s.newsletter_id === props.newsletter.id
  ) || undefined
);

// True when `subscriber` was matched from a subscribe-form submit (the 409
// "already exists" path) rather than a verified session. Recognition gates
// nothing (decision 0165), so these subscribers must sign in via magic link
// before they can manage — they are not treated as logged in.
const recognized = ref(false);

// State follows subscriber/auth by default, but can be imperatively
// overwritten for transient states (errors, validation). Resets
// automatically when the upstream (subscriber/auth) changes.
// isPending is layered on top so that the pending→settled transition
// doesn't reset error overrides set by onError.
const baseState = writable(() => {
  return calculateSubscriptionState(
    subscriber.value,
    props.subscriberFacingAuthentication
  );
});

const state = computed<SubscriptionState>(() => {
  if (
    subscribeMutation.isPending.value ||
    resubscribeMutation.isPending.value
  ) {
    return "pending_subscription";
  }
  return baseState.value;
});

const error = ref("");

watch(subscriber, (sub) => {
  error.value = "";
  if (sub) {
    emailAddress.value = sub.email_address;
  }
  // Reload to surface premium/gifted access only after a genuine subscribe
  // (checkout is proof of identity). A recognized-via-form subscriber isn't
  // authenticated, so reloading would just re-render them as logged out —
  // they get a magic-link sign-in prompt instead (B-4406).
  if (
    sub &&
    !recognized.value &&
    (sub.type === "premium" || sub.type === "gifted")
  ) {
    window.location.reload();
  }
});

// The captcha is a second-chance challenge shown after a firewall block. Once
// the visitor solves it, resubmit automatically — otherwise the form returns to
// the error state with no visible button and they're stuck (only Enter worked).
watch(turnstileResponseToken, (token, previousToken) => {
  if (token && !previousToken && baseState.value === "invalid_subscription") {
    subscribe();
  }
});

const subscribeMutation = useSubscribe({
  newsletter: props.newsletter,
  onSuccess(sub) {
    recognized.value = false;
    subscriber.value = sub;
    metadata.value = {};

    if (props.formId && Object.keys(surveyResponses.value).length > 0) {
      surveySubmission.mutate({
        subscriberId: sub.id,
        responses: surveyResponses.value,
      });
    }

    props.onSubscribe?.(sub);

    if (props.newsletter.subscription_redirect_url) {
      window.location.href = props.newsletter.subscription_redirect_url;
    }
  },
  onError(err, data) {
    if (data?.subscriber) {
      recognized.value = true;
      subscriber.value = {
        id: data.subscriber.id as string,
        email_address: data.subscriber.email_address as string,
        type: data.subscriber.type as SubscriptionState,
        newsletter_id: props.newsletter.id,
      };
    } else {
      const parsed = parseError(err, data);
      baseState.value = parsed.state;
      error.value = parsed.error;
      // Cloudflare/hCaptcha tokens are single-use. Drop the spent token so a
      // retry re-challenges for a fresh one instead of reusing a dead token.
      turnstileResponseToken.value = "";
    }
  },
});

const resubscribeMutation = useResubscribe({
  newsletter: props.newsletter,
  onSuccess(sub) {
    subscriber.value = sub;
  },
  onError(errorMessage) {
    baseState.value = "errored_email";
    error.value = errorMessage;
  },
});

const surveySubmission = useSurveySubmission();

function subscribe() {
  subscribeMutation.mutate({
    emailAddress: emailAddress.value,
    metadata: metadata.value,
    turnstileResponseToken: turnstileResponseToken.value,
    formId: props.formId || null,
  });
}

function resubscribe(subscriberId: string) {
  resubscribeMutation.mutate(subscriberId);
}

function checkSubscriber() {
  if (!emailAddress.value) return;
  if (!isValidEmail(emailAddress.value)) {
    baseState.value = "invalid_email__regex";
    error.value = t("web.form.validation.email_malformed");
    return;
  }
  if (subscriber.value) return;
  baseState.value = "not_subscribed";
}
</script>
